import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Star, Pin, Plus, Palette, Bell, Loader2,
} from 'lucide-react'
import {
  Input,
  Popover, PopoverTrigger, PopoverContent,
} from '@goodday-clone/ui'
import { cn } from '@/shared/utils/utils'
import { useAuth } from '@/context/AuthContext'
import type { Note, NoteColor } from './notesApi'
import { authorName, formatNoteTime, initialsOf, NOTE_COLORS, STICKY_STYLES } from './notesApi'
import type { CardActions } from './NoteCard'
import { NoteMenu } from './NoteCard'
import { parseTasks } from './markdown'

export interface BoardActions {
  onToggleTask: (note: Note, taskIndex: number) => void
  onAddTask: (note: Note, text: string) => void
  onAddSubtask: (note: Note, parentTaskIndex: number, text: string) => void
  onSetColor: (note: Note, color: NoteColor) => void
}

function stripInline(text: string) {
  return text
    .replace(/^#{1,3}\s+/gm, '')
    .replace(/[*_`>~-]/g, '')
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/\n+/g, ' ')
    .trim()
}

function ColorPicker({ note, disabled, onPick }: {
  note: Note
  disabled: boolean
  onPick: (color: NoteColor) => void
}) {
  const [open, setOpen] = useState(false)
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          onClick={(e) => e.stopPropagation()}
          disabled={disabled}
          className="p-1 rounded hover:bg-black/5 text-slate-500 disabled:opacity-40"
          title="Sticky color"
        >
          <Palette className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-auto p-2" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-1.5">
          {NOTE_COLORS.map((c) => (
            <button
              key={c}
              onClick={() => { onPick(c); setOpen(false) }}
              className={cn(
                'h-6 w-6 rounded-full border transition-transform hover:scale-110',
                STICKY_STYLES[c].bg,
                (note.color || 'YELLOW') === c && 'ring-2 ring-slate-500 ring-offset-1'
              )}
              title={c.charAt(0) + c.slice(1).toLowerCase()}
            />
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}

function TaskComposer({ placeholder, onSubmit, disabled }: {
  placeholder: string
  onSubmit: (text: string) => void
  disabled: boolean
}) {
  const [text, setText] = useState('')
  const submit = () => {
    const t = text.trim()
    if (!t) return
    onSubmit(t)
    setText('')
  }
  return (
    <Input
      value={text}
      disabled={disabled}
      onChange={(e) => setText(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') { e.preventDefault(); submit() }
        if (e.key === 'Escape') setText('')
      }}
      onBlur={submit}
      placeholder={placeholder}
      maxLength={200}
      className="h-7 text-xs bg-white/70 border-black/10 placeholder:text-slate-400 focus-visible:ring-emerald-400 disabled:opacity-50"
    />
  )
}

function StickyNote({ note, actions, board }: { note: Note; actions: CardActions; board: BoardActions }) {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [subtaskFor, setSubtaskFor] = useState<number | null>(null)

  const canEdit = actions.canEdit(note) && !note.deletedAt
  const style = STICKY_STYLES[note.color || 'YELLOW']
  const { tasks, rest } = parseTasks(note.content || '')
  const doneCount = tasks.filter((t) => t.done).length

  return (
    <div
      onClick={() => navigate(`/app/notepad/${note._id}`)}
      className={cn(
        'break-inside-avoid mb-4 rounded-lg border p-3.5 shadow-sm transition-shadow hover:shadow-md cursor-pointer',
        style.bg,
        note.deletedAt && 'opacity-60'
      )}
    >
      <div className="flex items-start gap-1.5">
        <h3 className="flex-1 min-w-0 text-sm font-semibold text-slate-900 leading-snug line-clamp-2" title={note.title || 'Untitled note'}>
          {note.isPinned && <Pin className="inline h-3.5 w-3.5 mr-1 text-emerald-700 fill-emerald-700 -mt-0.5" />}
          {note.title || 'Untitled note'}
        </h3>
        <div className="flex items-center shrink-0 -mt-0.5 -mr-1">
          {note.reminder?.enabled && note.reminder.at && !note.deletedAt && (
            <span title="Reminder set" className="p-1 text-slate-500">
              <Bell className="h-3.5 w-3.5" />
            </span>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); actions.onToggleStar(note) }}
            className="p-1 rounded hover:bg-black/5"
            title={note.isStarred ? 'Unstar' : 'Star'}
          >
            <Star className={cn('h-3.5 w-3.5', note.isStarred ? 'fill-amber-500 text-amber-500' : 'text-slate-400')} />
          </button>
          <ColorPicker note={note} disabled={!canEdit} onPick={(c) => board.onSetColor(note, c)} />
          <NoteMenu note={note} actions={actions} />
        </div>
      </div>

      {rest && (
        <p className="mt-2 text-[11px] leading-relaxed text-slate-600 line-clamp-4 break-words">
          {stripInline(rest)}
        </p>
      )}

      {tasks.length > 0 && (
        <div className="mt-2.5 space-y-1">
          {tasks.map((t) => (
            <div key={t.index} style={{ paddingLeft: `${Math.min(t.indent, 3) * 16}px` }}>
              <div className="group/task flex items-start gap-1.5">
                <input
                  type="checkbox"
                  checked={t.done}
                  disabled={!canEdit}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => board.onToggleTask(note, t.index)}
                  className="mt-0.5 h-3.5 w-3.5 shrink-0 rounded border-slate-400 accent-emerald-700"
                  title={`${doneCount}/${tasks.length} done — click to toggle`}
                />
                <span className={cn('flex-1 min-w-0 text-xs leading-snug break-words', t.done ? 'line-through text-slate-400' : 'text-slate-700')}>
                  {t.text}
                </span>
                {canEdit && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setSubtaskFor(subtaskFor === t.index ? null : t.index) }}
                    className="p-0.5 rounded opacity-0 group-hover/task:opacity-100 hover:bg-black/5 text-slate-500"
                    title="Add subtask"
                  >
                    <Plus className="h-3 w-3" />
                  </button>
                )}
              </div>
              {subtaskFor === t.index && (
                <div className="mt-1 pl-5" onClick={(e) => e.stopPropagation()}>
                  <TaskComposer
                    placeholder="Subtask, press Enter"
                    onSubmit={(text) => { board.onAddSubtask(note, t.index, text); setSubtaskFor(null) }}
                    disabled={!canEdit}
                  />
                </div>
              )}
            </div>
          ))}
          <p className="text-[10px] text-slate-500 pt-0.5">{doneCount}/{tasks.length} done</p>
        </div>
      )}

      {canEdit && (
        <div className="mt-2.5" onClick={(e) => e.stopPropagation()}>
          <TaskComposer placeholder="Add a task…" onSubmit={(text) => board.onAddTask(note, text)} disabled={!canEdit} />
        </div>
      )}

      <div className="mt-3 pt-2 border-t border-black/5 flex items-center justify-between gap-2">
        <span className="text-[10px] text-slate-500 truncate">{formatNoteTime(note.updatedAt)}</span>
        <span
          className="h-5 w-5 shrink-0 rounded-full bg-slate-700 text-white text-[9px] font-semibold flex items-center justify-center"
          title={authorName(note, user?.id)}
        >
          {initialsOf(authorName(note, user?.id))}
        </span>
      </div>
    </div>
  )
}

export default function NoteBoard({ notes, actions, board, busy }: {
  notes: Note[]
  actions: CardActions
  board: BoardActions
  busy: boolean
}) {
  return (
    <div className="relative">
      {busy && (
        <div className="absolute right-0 -top-1 flex items-center gap-1 text-[11px] text-slate-400">
          <Loader2 className="h-3 w-3 animate-spin" /> Saving…
        </div>
      )}
      <div className="columns-1 sm:columns-2 xl:columns-3 2xl:columns-4 gap-4">
        {notes.map((note) => (
          <StickyNote key={note._id} note={note} actions={actions} board={board} />
        ))}
      </div>
    </div>
  )
}
